/**
 * **Los desplegables del formulario de la cotización, y la obra que se crea desde él.**
 *
 * Tienen archivo propio porque son los tipos más flacos del módulo y los que más cambian: cada
 * uno declara **solo las columnas que la pantalla usa** de un recurso genérico (D-026), no la
 * tabla. Metidos entre los contratos de verdad parecían contrato, y quien los leía así creía que
 * una columna que falta es un incumplimiento del servidor.
 */

/* ── Los desplegables del formulario, TODOS locales ─────────────────────────────
 *
 * Ninguno le habla a Siigo. El formulario viejo buscaba productos con `/api/siigo/products`, y
 * eso es una petición a Siigo: el cupo es **de la empresa**, no del programa, y Siigo bloquea el
 * usuario si se abusa — un usuario que es el de una persona que trabaja con él. Además
 * `quote_line` pide una `reference_id` local, así que un producto de Siigo no serviría ni
 * queriendo.
 *
 * Los tres salen de recursos genéricos, cuyas columnas vienen de la base (D-026). Por eso aquí
 * solo se declara **lo que la pantalla usa**, no la tabla entera: declarar de más obligaría a
 * tocar este archivo cada vez que una migración añade una columna.
 */

export type Project = {
  id: string;
  name: string;
  city: string | null;
  address: string | null;
  status: string | null;
};

export type ProductReference = {
  id: string;
  name: string;
  siigo_code: string | null;
  kind: string | null;
  /**
   * El formato de la referencia. Hace falta para el costeo de la lisa, que se pide **por formato**
   * (`/costs/lisa-grid?format_id=`) y no por referencia. Llega porque el recurso genérico devuelve
   * la fila entera; se declara aquí porque la pantalla lo usa, no porque la tabla lo tenga.
   */
  format_id: string | null;
  /**
   * El color de la referencia, **cuando lo tiene**.
   *
   * La lisa no se cataloga por color —una referencia no es un SKU—, así que «Baldosa Lisa 20×20»
   * llega con `color_id` nulo y el color se decide en la línea. Por eso este campo no es un adorno:
   * es **la pregunta de si a esta línea le falta escoger color**. Sin él, un pedido de lisa nace sin
   * decir de qué color, y quien lo descubre es la planta el día que va a mezclar.
   */
  color_id: string | null;
  is_active: boolean;
};

/**
 * **La referencia neutra de la baldosa lisa 20×20.**
 *
 * La lisa no se cataloga por color: son 36 colores sobre el mismo diseño, y tener una referencia
 * por color daría un catálogo de miles de filas que nadie mantiene (la regla de siempre: una
 * referencia no es un SKU). Así que existe **una** referencia sin color —«Baldosa Lisa 20×20»,
 * formato «Baldosa 20×20»— y el color se elige en la línea de la cotización.
 *
 * Se nombra por su id a propósito: no hay ninguna columna que diga «esta es la lisa». Cuando la
 * haya —un `kind` propio, una marca en la ficha— este constante se cambia por esa pregunta, y
 * entonces la sugerencia de precio valdrá para todas las líneas lisas y no solo para esta.
 */
export const LISA_20X20_REFERENCE_ID = "f74d6719-c3a1-4ac6-b7e2-421f730fe1db";

export type ColorRow = {
  id: string;
  name: string;
  code: string | null;
  hex: string | null;
};

export type Page<T> = { rows: T[]; total: number; limit: number; offset: number };

/**
 * Lo que hace falta para crear una obra: **solo el nombre**.
 *
 * Dirección y ciudad son opcionales a propósito. Este formulario aparece en mitad de un cobro, y
 * exigir más datos ahí hace que alguien escriba cualquier cosa por salir del paso — y una obra
 * mal nombrada se arrastra para siempre.
 */
export type NuevaObra = {
  customer_id: string;
  name: string;
  address?: string | null;
  city?: string | null;
};
